import { createSelector, createSlice } from '@reduxjs/toolkit';
import { selectAllPoints } from '../map/slice/mapSelectors';

/**
 * @typedef {{
 *  tsp: number[];
 *  rate_schedule_type: string[];
 *  raw_rate_schedule: string[];
 *  owner: string[];
 *  agent_name: string[];
 *  type: string[];
 *  k_id: string;
 *  receipt_points: string;
 *  receipt_zones: int[];
 *  delivery_points: string;
 *  delivery_zones: int[];
 * }} IOCContractsFilter
 */

/**
 * @typedef {{
 *  contracts: import('@/types/api/IOCContract').IOCContract[];
 *  visibility: {[key: number]: boolean};
 *  filters: IOCContractsFilter;
 * }} IOCState
 */

// ================================ Initial State ================================ //

/**
 * @type {IOCState}
 */
const initialState = {
  contracts: [],
  visibility: {},
  filters: {
    tsp: [],
    owner: [],
    agent_name: [],
    rate_schedule_type: [],
    raw_rate_schedule: [],
    type: [],
    k_id: '',
    receipt_points: '',
    delivery_points: '',
    delivery_zones: [],
    receipt_zones: [],
  },
};

// ================================ Private Methods ================================ //

// ================================ Public Methods ================================ //

// ================================ Reducers ================================ //

/**
 * @param {IOCState} initialState
 */
const resetState = (initialState) => initialState;

/**
 *
 * @param {IOCState} state
 * @param {{payload: import('@/types/api/IOCContract').IOCContract[]}} action
 * @returns {void}
 */
const setContracts = (state, action) => {
  state.contracts = action.payload;
  state.visibility = action.payload.reduce((acc, contract) => {
    acc[contract.id] = true;
    return acc;
  }, {});
};

/**
 *
 * @param {IOCState} state
 * @param {{
 *  payload: {
 *    contracts: import('@/types/api/IOCContract').IOCContract[];
 *    visible: boolean;
 *  }
 * }} action
 */
const setVisibility = (state, action) => {
  const { contracts, visible } = action.payload;
  for (const contract of contracts) {
    state.visibility[contract.id] = visible;
  }
};

/**
 *
 * @param {IOCState} state
 * @param {{
 *  payload: {
 *    key: keyof IOCContractsFilter;
 *    value: string | string[];
 *  }
 * }} action
 */
const setFilter = (state, action) => {
  const { key, value } = action.payload;
  state.filters[key] = value;
};

/**
 *
 * @param {IOCState} initialState
 */
const _iocReducers = (initialState) => ({
  resetState: () => resetState(initialState),
  setContracts,
  setVisibility,
  setFilter,
});

const reducers = _iocReducers(initialState);

/** @type {import('@reduxjs/toolkit').Slice<IOCState, typeof reducers, 'ioc'>} */
export const iocSlice = createSlice({
  name: 'ioc',
  initialState,
  reducers,
});

export const iocActions = iocSlice.actions;

// ================================ Selectors ================================ //

/**
 * @param {{
 *  ioc: IOCState;
 * }} state
 */
const selectState = (state) => state?.ioc ?? initialState;

export const selectIOCContracts = createSelector(
  selectState,
  (state) => state.contracts ?? [],
);

export const selectSortedIOCContracts = createSelector(
  selectIOCContracts,
  (contracts) => {
    return [...contracts].sort((a, b) =>
      a.owner
        .toLocaleLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .localeCompare(b.owner.toLocaleLowerCase().replace(/[^a-z0-9]/g, '')),
    );
  },
);

export const selectIOCVisibility = createSelector(
  selectState,
  (state) => state.visibility,
);

export const selectIOCFilters = createSelector(
  selectState,
  (state) => state.filters,
);

export const selectFilteredIOCContracts = createSelector(
  selectSortedIOCContracts,
  selectIOCFilters,
  selectAllPoints,
  (contracts, filters, points) => {
    return contracts.filter((contract) => {
      return Object.entries(filters ?? {}).every(([key, value]) => {
        if (value === '' || (Array.isArray(value) && value.length === 0))
          return true;

        if (key === 'receipt_points' || key === 'delivery_points') {
          return contract[key.replace('_points', '_volumes')]
            .map(({ point, _vol }) => points[point]?.loc_name ?? '')
            .some((v) => v.toLowerCase().includes(value.toLowerCase()));
        }

        if (key === 'receipt_zones') {
          return filters[key].some((zoneId) =>
            contract.receipt_volumes
              .map((receipt) => points[receipt.point])
              .some((point) => point?.receipt_zone === zoneId),
          );
        }
        if (key === 'delivery_zones') {
          return filters[key].some((zoneId) =>
            contract.delivery_volumes
              .map((delivery) => points[delivery.point])
              .some((point) => point?.delivery_zone === zoneId),
          );
        }

        if (key === 'tsp') {
          return filters.tsp.some((tspId) => contract.tsp.id === tspId);
        }

        if (Array.isArray(value)) {
          return value.some((item) =>
            contract[key]
              ?.toString()
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '')
              .includes(item.toLowerCase().replace(/[^a-z0-9]/g, '')),
          );
        }

        return contract[key]
          ?.toString()
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
          .includes(value.toLowerCase().replace(/[^a-z0-9]/g, ''));
      });
    });
  },
);

export default iocSlice.reducer;
